import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchProjects();
  }, [selectedStatus, searchTerm, currentPage]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 12,
        status: selectedStatus,
        search: searchTerm
      };

      const response = await axios.get('/api/projects', { params });
      setProjects(response.data.projects);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      ongoing: { text: 'Ongoing', class: 'bg-success', icon: '🟢' },
      completed: { text: 'Completed', class: 'bg-primary', icon: '🔵' },
      planned: { text: 'Planned', class: 'bg-warning', icon: '🟡' }
    };
    const badge = badges[status] || badges.planned;
    return (
      <span className={`badge ${badge.class} text-white px-3 py-2`} style={{ fontSize: '0.85rem' }}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    adaptiveHeight: true
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProjects();
  };

  if (loading && projects.length === 0) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div
        className="container-fluid"
        style={{
          background: 'linear-gradient(rgba(var(--tgh-blue-rgb), 0.9), rgba(var(--tgh-blue-rgb), 0.9)), url(/img/hospital-building.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '4rem 0 3rem',
        }}
      >
        <div className="container">
          <div className="text-center">
            <h1 className="text-white mb-0" style={{ fontSize: '2.5rem', fontWeight: '600', fontFamily: "'Lora', 'Georgia', serif" }}>
              Projects & Ongoing Initiatives
            </h1>
            <p className="text-white mt-3 mb-0" style={{ fontSize: '1.1rem', opacity: 0.9 }}>
              Discover our impactful projects and initiatives
            </p>
            <nav aria-label="breadcrumb" className="mt-3 d-flex justify-content-center">
              <ol className="breadcrumb mb-0" style={{ backgroundColor: 'transparent', padding: 0 }}>
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">
                  Projects
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Projects Section */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa', minHeight: '60vh' }}>
        <div className="container">
          {/* Filters */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="projects-filter-container d-flex flex-wrap gap-3 align-items-center mb-3">
                <div className="projects-status-filter d-flex flex-wrap gap-2">
                  <button
                    className={`btn ${selectedStatus === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handleStatusChange('all')}
                    style={{ borderRadius: '4px', fontSize: '0.9rem' }}
                  >
                    All
                  </button>
                  <button
                    className={`btn ${selectedStatus === 'ongoing' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => handleStatusChange('ongoing')}
                    style={{ borderRadius: '4px', fontSize: '0.9rem' }}
                  >
                    🟢 Ongoing
                  </button>
                  <button
                    className={`btn ${selectedStatus === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handleStatusChange('completed')}
                    style={{ borderRadius: '4px', fontSize: '0.9rem' }}
                  >
                    🔵 Completed
                  </button>
                  <button
                    className={`btn ${selectedStatus === 'planned' ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => handleStatusChange('planned')}
                    style={{ borderRadius: '4px', fontSize: '0.9rem' }}
                  >
                    🟡 Planned
                  </button>
                </div>
                
                {/* Search */}
                <form onSubmit={handleSearch} className="projects-search-form d-flex gap-2 ms-auto" style={{ flex: '1', maxWidth: '400px' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ borderRadius: '4px' }}
                  />
                  <button type="submit" className="btn btn-primary" style={{ borderRadius: '4px' }}>
                    <i className="bi bi-search"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Projects Grid */}
          <div className="row g-4">
            {projects.length === 0 ? (
              <div className="col-12 text-center py-5">
                <p className="text-muted" style={{ fontSize: '1rem' }}>No projects found.</p>
              </div>
            ) : (
              projects.map((project) => {
                // Collect all images: featured image + gallery images
                const allImages = [];
                
                // Add featured image first
                if (project.featured_image) {
                  const featuredUrl = project.featured_image.startsWith('/uploads/') 
                    ? project.featured_image 
                    : `/uploads/${project.featured_image}`;
                  allImages.push({ image: featuredUrl, isFeatured: true });
                }
                
                // Add gallery images
                if (project.images && project.images.length > 0) {
                  project.images.forEach(img => {
                    const imgUrl = img.image.startsWith('/uploads/') 
                      ? img.image 
                      : `/uploads/${img.image}`;
                    allImages.push({ image: imgUrl, caption: img.caption });
                  });
                }
                
                // If no images, use default
                if (allImages.length === 0) {
                  allImages.push({ image: '/img/Gallery/opd1.jpg' });
                }

                return (
                  <div key={project.id} className="col-xl-4 col-lg-6 col-md-6 col-12">
                    <div
                      className="card h-100 shadow-sm project-card"
                      style={{
                        borderRadius: '12px',
                        overflow: 'hidden',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        border: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                      }}
                    >
                      <div className="tgh-card-media">
                        {allImages.length > 1 ? (
                          <Slider {...sliderSettings}>
                            {allImages.map((img, index) => (
                              <div key={index}>
                                <img
                                  src={img.image}
                                  alt={project.title}
                                  className="card-img-top"
                                  onError={(e) => {
                                    e.target.src = '/img/Gallery/opd1.jpg';
                                  }}
                                />
                              </div>
                            ))}
                          </Slider>
                        ) : (
                          <img
                            src={allImages[0].image}
                            alt={project.title}
                            className="card-img-top"
                            onError={(e) => {
                              e.target.src = '/img/Gallery/opd1.jpg';
                            }}
                          />
                        )}
                      </div>

                      {/* Card Body */}
                      <div className="card-body d-flex flex-column">
                        <div className="mb-2">
                          {getStatusBadge(project.status)}
                        </div>
                        
                        <h5 className="card-title mb-2" style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a' }}>
                          {project.title}
                        </h5>
                        
                        <p className="card-text text-muted mb-3" style={{ fontSize: '0.95rem', lineHeight: '1.6', flex: '1' }}>
                          {project.short_description || project.description?.substring(0, 120) + '...' || 'No description available.'}
                        </p>

                        {/* Progress Bar for Ongoing Projects */}
                        {project.status === 'ongoing' && project.progress !== null && (
                          <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small className="text-muted">Progress</small>
                              <small className="text-muted fw-bold">{project.progress}%</small>
                            </div>
                            <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                              <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{ width: `${project.progress}%` }}
                                aria-valuenow={project.progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>
                          </div>
                        )}

                        {/* Dates */}
                        <div className="mb-3">
                          <small className="text-muted d-block">
                            <i className="bi bi-calendar3 me-1"></i>
                            Start: {formatDate(project.start_date)}
                          </small>
                          {project.end_date && (
                            <small className="text-muted d-block">
                              <i className="bi bi-calendar-check me-1"></i>
                              End: {formatDate(project.end_date)}
                            </small>
                          )}
                        </div>

                        <Link
                          to={`/projects/${project.id}`}
                          className="btn btn-primary mt-auto"
                          style={{ borderRadius: '6px', fontSize: '0.9rem' }}
                        >
                          View Details <i className="bi bi-arrow-right ms-1"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="row mt-5">
              <div className="col-12">
                <nav aria-label="Projects pagination">
                  <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                        Previous
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => (
                      <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                          {i + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default Projects;
